import { Component, inject, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { DatePickerModule } from 'primeng/datepicker';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { NotificationService } from 'src/app/core/services/notification.service';
import { AdminUpdateNoteService } from '../../services/update-note.service';
import {
  UPDATE_NOTE_TYPES,
  UpdateNoteDto,
  UpdateNoteType,
} from 'src/app/update-notes/types/update-note';

@Component({
  selector: 'app-edit-update-note',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    SelectModule,
    TextareaModule,
    DatePickerModule,
    ProgressSpinnerModule,
  ],
  templateUrl: './edit-update-note.component.html',
})
export class EditUpdateNoteComponent implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  private updateNoteService = inject(AdminUpdateNoteService);
  private notificationService = inject(NotificationService);

  noteId: string | null = null;
  isLoading = false;
  isSaving = false;

  readonly typeOptions = UPDATE_NOTE_TYPES.map((type) => ({
    label: type === UpdateNoteType.PLATFORM ? 'Platform' : 'Emission factors',
    value: type,
  }));

  form = this.fb.nonNullable.group({
    type: this.fb.control<UpdateNoteType | null>(null, Validators.required),
    releasedAt: this.fb.control<Date | null>(new Date(), Validators.required),
    contentFR: ['', Validators.required],
    contentEN: ['', Validators.required],
    contentNL: ['', Validators.required],
  });

  get isEditMode(): boolean {
    return this.noteId !== null;
  }

  ngOnInit(): void {
    this.noteId = this.route.snapshot.paramMap.get('id');
    if (this.noteId) {
      this.loadNote(this.noteId);
    }
  }

  private loadNote(id: string): void {
    this.isLoading = true;
    this.updateNoteService.getNoteById(id).subscribe({
      next: (note) => {
        this.form.patchValue({
          type: note.type,
          releasedAt: new Date(note.releasedAt),
          contentFR: note.contentFR,
          contentEN: note.contentEN,
          contentNL: note.contentNL,
        });
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.notificationService.error('Could not load the update note');
        this.router.navigate(['/admin/update-notes']);
      },
    });
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const payload: UpdateNoteDto = {
      type: value.type!,
      releasedAt: this.formatDateForBackend(value.releasedAt!),
      contentFR: value.contentFR,
      contentEN: value.contentEN,
      contentNL: value.contentNL,
    };

    this.isSaving = true;
    const request$ = this.noteId
      ? this.updateNoteService.updateNote(this.noteId, payload)
      : this.updateNoteService.createNote(payload);

    request$.subscribe({
      next: () => {
        this.isSaving = false;
        this.notificationService.success(
          this.isEditMode ? 'Update note saved' : 'Update note created',
        );
        this.router.navigate(['/admin/update-notes']);
      },
      error: () => {
        this.isSaving = false;
        this.notificationService.error('Could not save the update note');
      },
    });
  }

  cancel(): void {
    this.location.back();
  }

  private formatDateForBackend(date: Date): string {
    // VineJS date validator expects YYYY-MM-DD format, not full ISO string
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
